import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, zip } from 'rxjs';
import { EmployerService } from './employer.service';
import { ErrorHandlerService } from './error-handler.service';
import { DeadlineV2Dto, DeadlineRestService, EmployerDto, EventV2Dto } from '../api/exa';
import { PageRequestDto } from '../../app/api/exa/model/pageRequestDto';
import { AuthService } from './auth.service';
import { NGXLogger } from 'ngx-logger';
import { MachineService } from './machine.service';
import { NotificationTypeEnum } from '../enums/notification-type-enum';
import { SQLQueryParamOperatorEnum } from '../enums/sql-query-param-operator-enum';
import { PubSubService } from './pubsub.service';

export class Notification {
  timestamp: string;
  type: string;
  payload: any | string | DeadlineV2Dto | EventV2Dto;
  priority: number; // higher number -> higher priority
}

export enum Priority {
  LOWEST = 0,
  LOW = 1,
  MIDDLE = 2,
  HIGH = 3,
  HIGHEST = 4,
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private employerDto: EmployerDto = null;

  private onNotificationsChanged$ = new BehaviorSubject<Notification[]>([]);

  // TODO maybe a list for each notification type?
  // - Deadlines
  // - Messages
  // - General (like machines long offline?)

  private deadlines: DeadlineV2Dto[] = [];
  private events: EventV2Dto[] = [];
  private licenseExpiration: Notification = null;
  private longOfflineMachines: Notification = null;

  constructor(
    private access: AuthService,
    private employerService: EmployerService,
    private machineService: MachineService,
    private pubSubService: PubSubService,
    private deadlineRest: DeadlineRestService,
    private errorHandler: ErrorHandlerService,
    private log: NGXLogger
  ) {
    this.employerService.onSelectEmployer().subscribe((employer) => {
      // check if employer changed
      if (!this.employerDto || this.employerDto.id !== employer.id) {
        // employer changed or was removed
        this.deadlines = [];
        this.events = [];
        this.licenseExpiration = null;
        this.longOfflineMachines = null;

        this.onNotificationsChanged$.next(this.getNotifications());

        this.employerDto = employer;
        if (this.employerDto) {
          this.getDeadlines();
        }
      }
      // else do nothing
    });

    this.employerService.onActivatedLicensesChanged().subscribe((license) => {
      if (license) {
        // Check if License is about to expire
        const expiresInDays = Math.floor((Number(license.expiration) - new Date().getTime()) / 86400000);
        if (expiresInDays >= 0 && expiresInDays <= 12 && !this.licenseExpiration) {
          this.licenseExpiration = {
            timestamp: new Date().getTime().toString(),
            type: NotificationTypeEnum.LICENSE_EXPIRATION,
            payload: expiresInDays,
            priority: Priority.HIGHEST,
          };
          this.onNotificationsChanged$.next(this.getNotifications());
        }
      }
    });

    this.machineService.onGetMachines().subscribe((machines) => {
      // check if there are offline machines and trigger notification if needed
      if (this.machineService.hasLongOfflineMachine() && !this.longOfflineMachines) {
        this.longOfflineMachines = {
          timestamp: new Date().getTime().toString(),
          type: NotificationTypeEnum.MACHINES_LONG_OFFLINE,
          payload: this.machineService.getLongOfflineMachineCount(),
          priority: Priority.HIGH,
        };
        this.onNotificationsChanged$.next(this.getNotifications());
      }
    });

    this.pubSubService.onEvents().subscribe((event) => {
      this.events.push(event[1]);
      this.onNotificationsChanged$.next(this.getNotifications());
    });

    this.log.debug('Construct');
  }

  onNotificationsChanged(): Observable<Notification[]> {
    return this.onNotificationsChanged$;
  }

  getNotifications(): Notification[] {
    const notifications: Notification[] = [];
    if (this.licenseExpiration) {
      notifications.push(this.licenseExpiration);
    }
    if (this.longOfflineMachines) {
      notifications.push(this.longOfflineMachines);
    }
    // convert the deadlines
    for (const d of this.deadlines) {
      let prio = Priority.MIDDLE;
      if (d.status === 'D') {
        prio = Priority.HIGH;
      }
      const n: Notification = {
        timestamp: new Date().getTime().toString(),
        type: NotificationTypeEnum.DEADLINE,
        payload: d,
        priority: prio,
      };
      notifications.push(n);
    }
    // convert the events
    for (const e of this.events) {
      const n: Notification = { timestamp: e.time, type: NotificationTypeEnum.EVENT, payload: e, priority: Priority.LOW };
      notifications.push(n);
    }

    return notifications;
  }

  getDeadlines() {
    const deadlineRequestW: PageRequestDto = {
      filter: [{ name: 'status', operator: SQLQueryParamOperatorEnum.EQ, values: [new Object('W')] }],
      page: 0,
      sort: [
        {
          direction: 'desc',
          property: 'id',
        },
      ],
      size: 50,
    };
    const deadlineRequestD: PageRequestDto = {
      filter: [{ name: 'status', operator: SQLQueryParamOperatorEnum.EQ, values: [new Object('D')] }],
      page: 0,
      sort: [
        {
          direction: 'desc',
          property: 'id',
        },
      ],
      size: 50,
    };

    zip(
      this.deadlineRest.getDeadlinesUsingPOST(this.employerDto.username, deadlineRequestW),
      this.deadlineRest.getDeadlinesUsingPOST(this.employerDto.username, deadlineRequestD)
    ).subscribe(
      (data) => {
        // handle warn deadlines
        for (const deadline of data[0].content) {
          // just to be sure -> there may was a change in between
          if (deadline.owner.username === this.employerDto.username) {
            this.deadlines.push(deadline);
          }
        }
        // handle due deadlines
        for (const deadline of data[1].content) {
          // just to be sure -> there may was a change in between
          if (deadline.owner.username === this.employerDto.username) {
            this.deadlines.push(deadline);
          }
        }
        this.log.debug('Deadlines warn:', data[0].content);
        this.log.debug('Deadlines due:', data[1].content);
        this.log.debug('Deadlines combined:', this.deadlines);
      },
      (err) => {
        this.errorHandler.handleHttpError(err);
      }
    );
  }
}
